import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";

const Terms = () => {
  return (
    <Layout>
      {/* GENERATED META — review/refine title + description. */}
      <Seo
        title="Terms and Conditions | Blue Heron Marketing"
        description="Read the terms and conditions for using Blue Heron Marketing's website and services."
        path="/terms"
      />
      <div className="py-20">
        <div className="container mx-auto px-6">
          <article className="max-w-4xl mx-auto prose prose-lg">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              Terms and Conditions
            </h1>
            
            <p className="text-xl font-semibold text-foreground mb-8">
              Blue Heron
            </p>
            
            <p className="text-muted-foreground mb-8">
              Last Updated: 1/30/2026
            </p>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">1. Agreement to Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By accessing and using Blue Heron Marketing's services, you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may not access our services.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">2. Services Provided</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Blue Heron Marketing provides digital marketing services including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Website design and development</li>
                <li>Search Engine Optimization (SEO)</li>
                <li>Google Business Profile management</li>
                <li>Google Ads management</li>
                <li>Digital marketing consulting</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">3. Service Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All services are provided under specific agreements outlined in individual service contracts. The scope, timeline, and deliverables will be clearly defined before work begins.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Results may vary based on numerous factors including market conditions, competition, and client cooperation. While we strive for excellence, we do not guarantee specific outcomes.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">4. Payment Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Payment terms will be outlined in your service agreement. Generally:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Payments are due according to the schedule in your contract</li>
                <li>Late payments may result in service suspension</li>
                <li>Refunds are handled on a case-by-case basis as outlined in your agreement</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">5. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Upon full payment, clients retain ownership of custom work created specifically for them. Blue Heron Marketing retains the right to use completed work in portfolios and marketing materials unless otherwise specified.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">6. Client Responsibilities</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Clients are responsible for:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li>Providing timely feedback and approvals</li>
                <li>Supplying necessary access, content, and materials</li>
                <li>Maintaining accurate account information</li>
                <li>Complying with all applicable laws and regulations</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">7. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Blue Heron Marketing shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services. Our total liability shall not exceed the amount paid for services in the preceding 12 months.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">8. Termination</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Either party may terminate services according to the terms outlined in the service agreement. Typically, a 30-day written notice is required.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">9. SMS/Text Message Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By opting in to receive SMS text messages from Blue Heron, you agree to the following:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
                <li><strong>Message Sender:</strong> Blue Heron</li>
                <li><strong>Types of Messages:</strong> You may receive transactional messages (appointment reminders, account notifications, service updates) and/or promotional messages (marketing offers, newsletters, special promotions) based on your consent preferences</li>
                <li><strong>Message Frequency:</strong> Message frequency varies based on your account activity and preferences. You may receive up to 4 transactional messages per month.</li>
                <li><strong>Message & Data Rates:</strong> Standard message and data rates may apply. Contact your wireless carrier for details about your text plan</li>
                <li><strong>Age Requirement:</strong> You must be 18 years of age or older to opt in to receive SMS messages</li>
                <li><strong>How to Get Help:</strong> Text <strong>HELP</strong> to receive help information or <Link to="/contact" className="text-primary hover:underline">contact us</Link></li>
                <li><strong>How to Opt Out:</strong> Text <strong>STOP</strong> at any time to opt out of receiving messages. You will receive a confirmation message</li>
                <li><strong>Consent Not Required:</strong> Consent to receive SMS messages is not a condition of purchasing any goods or services</li>
                <li><strong>Supported Carriers:</strong> Supported carriers include AT&T, Verizon, T-Mobile, Sprint, and most major U.S. carriers. Message delivery is subject to effective transmission from your wireless carrier</li>
                <li><strong>Privacy:</strong> We will not share your SMS consent or phone number with third parties for their marketing purposes, except as necessary to deliver messages through our SMS service providers</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">10. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Continued use of our services constitutes acceptance of modified terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold mb-4">11. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                For questions about these Terms and Conditions, please <Link to="/contact" className="text-primary hover:underline">contact us</Link>.
              </p>
            </section>
          </article>
        </div>
      </div>
    </Layout>
  );
};

export default Terms;
